import { useState, useEffect, useRef } from "react";
import { T, STATE, TIER, FEATURE_PLAN, getStateConfig } from "../../config/theme.js";
import { Lock, Pause as PauseIcon, HelpCircle } from "lucide-react";
import * as LucideIcons from "lucide-react"; // SectionTitle 동적 아이콘용

// ═══ 글로벌 스타일은 App.jsx에서 1회만 렌더링 ═══
// mono-num, label-micro, pulse-dot 클래스는 App.jsx <style> 블록에 정의됨

// ═══ StateBadge (리디자인: Lucide 아이콘 + Pulse dot) ═══
export function StateBadge({ state, size = "sm" }) {
  const c = getStateConfig(state);
  const sz = size === "lg"
    ? { fontSize: 13, padding: "6px 14px", dotSize: 10 }
    : { fontSize: 11, padding: "4px 10px", dotSize: 8 };

  return (
    <span style={{
      fontSize: sz.fontSize, padding: sz.padding,
      background: "transparent", color: T.text,
      borderRadius: 20, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 6,
      border: `1px solid ${c.color}30`,
    }}>
      <span style={{
        display: "inline-block", width: sz.dotSize, height: sz.dotSize,
        borderRadius: "50%", background: c.color, flexShrink: 0,
        animation: state !== "STABLE" && state !== "HOLD" ? "pulse-dot 2s ease-in-out infinite" : "none",
      }} />
      {c.label}
    </span>
  );
}

// ═══ Bar (리디자인: 더 세련된 프로그래스) ═══
export function Bar({ label, value, color, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: T.textDim, fontWeight: 500 }}>{label}</span>
        <span className="mono-num" style={{ fontSize: 14, color, fontWeight: 700 }}>
          {value}{sub && <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 3 }}>{sub}</span>}
        </span>
      </div>
      <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
        <div style={{
          height: "100%", width: `${Math.min(Math.max(value, 0), 100)}%`,
          background: `linear-gradient(90deg, ${color}, ${color}CC)`,
          borderRadius: 2, transition: "width 0.8s cubic-bezier(0.22,1,0.36,1)",
        }} />
      </div>
    </div>
  );
}

// ═══ Card (리디자인: variant hero/base 지원) ═══
export function Card({ children, variant = "base", style = {}, onClick, hover }) {
  const [h, setH] = useState(false);
  const isHero = variant === "hero";
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        background: isHero
          ? `linear-gradient(135deg, ${T.card}, rgba(59,130,246,0.03))`
          : (h && hover ? T.cardHover : T.card),
        border: `1px solid ${isHero ? T.accent + "25" : (h && hover ? T.accent + "40" : T.border)}`,
        borderRadius: 16,
        padding: isHero ? "28px 24px" : "24px",
        boxShadow: isHero ? "0 4px 24px rgba(0,0,0,0.15)" : "none",
        transition: "all 0.2s ease-in-out",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}>
      {children}
    </div>
  );
}

// ═══ Tip (툴팁 — 기존 유지) ═══
export function Tip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 5 }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: T.textMuted + "25", color: T.textMuted, fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "help" }}>?</span>
      {show && (
        <span style={{ position: "absolute", bottom: "130%", left: "50%", transform: "translateX(-50%)", background: "#1a1a28", border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: T.text, fontWeight: 500, whiteSpace: "nowrap", zIndex: 999, boxShadow: "0 8px 24px rgba(0,0,0,0.6)", maxWidth: 300, lineHeight: 1.5 }}>
          {text}
        </span>
      )}
    </span>
  );
}

// ═══ SectionTitle (리디자인: Lucide 아이콘 모듈 + fontWeight 정리) ═══
export function SectionTitle({ children, tip, badge, icon }) {
  const IconCmp = icon ? LucideIcons[icon] : null;
  return (
    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
      {IconCmp && (
        <span style={{ display: "inline-flex", padding: 6, background: T.border, borderRadius: 8, color: T.textDim }}>
          <IconCmp size={16} strokeWidth={1.5} />
        </span>
      )}
      {tip ? <Tip text={tip}>{children}</Tip> : children}
      {badge && (
        <span style={{
          fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 700,
          border: `1px solid ${badge === "PRO" ? T.accent : T.green}40`,
          color: badge === "PRO" ? T.accent : T.green,
        }}>{badge}</span>
      )}
    </div>
  );
}

// ═══ MiniChart (SVG 스파크라인 — 기존 유지 + mono-num 적용) ═══
export function MiniChart({ data, color = T.accent, height = 40, width = "100%", label, showDots = false }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => typeof d === "number" ? d : d.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 200;
  const h = height;
  const pad = 2;

  const points = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });

  const latest = vals[vals.length - 1];
  const first = vals[0];
  const change = first !== 0 ? ((latest - first) / Math.abs(first) * 100).toFixed(1) : 0;
  const trendColor = latest >= first ? T.green : T.red;

  return (
    <div style={{ width }}>
      {label && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{label}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="mono-num" style={{ fontSize: 13, fontWeight: 700, color: trendColor }}>{latest?.toFixed?.(2) ?? latest}</span>
            <span className="mono-num" style={{ fontSize: 10, color: trendColor, fontWeight: 600 }}>{change > 0 ? "+" : ""}{change}%</span>
          </div>
        </div>
      )}
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height }}>
        <defs>
          <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={`${pad},${h - pad} ${points.join(" ")} ${w - pad},${h - pad}`} fill={`url(#grad-${label})`} />
        <polyline points={points.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {(() => {
          const lastPt = points[points.length - 1]?.split(",");
          return lastPt ? <circle cx={lastPt[0]} cy={lastPt[1]} r="3" fill={color} /> : null;
        })()}
      </svg>
      <div className="mono-num" style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.textMuted }}>
        <span>{data[0]?.date || `${data.length}일 전`}</span>
        <span>{data[data.length - 1]?.date || "오늘"}</span>
      </div>
    </div>
  );
}

// ═══ Btn (기존 유지) ═══
export function Btn({ children, variant = "primary", style = {}, ...props }) {
  const base = { border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 };
  const v = {
    primary: { ...base, background: T.accent, color: "#fff", padding: "10px 22px" },
    ghost: { ...base, background: "transparent", color: T.textDim, padding: "8px 16px", border: `1px solid ${T.border}` },
    success: { ...base, background: T.green, color: "#fff", padding: "10px 22px" },
    small: { ...base, background: T.accent, color: "#fff", padding: "6px 14px", fontSize: 12 },
    landing: { ...base, background: T.accent, color: "#fff", padding: "16px 40px", fontSize: 16, borderRadius: 12, fontWeight: 700 },
    outline: { ...base, background: "transparent", color: T.text, padding: "12px 28px", border: `1px solid ${T.border}`, borderRadius: 10 },
  };
  return <button style={{ ...v[variant], ...style }} {...props}>{children}</button>;
}

// ═══ Input (기존 유지) ═══
export function Input({ style = {}, ...props }) {
  return <input style={{ background: T.card, color: T.text, border: `1px solid ${T.border}`, padding: "12px 16px", borderRadius: 10, fontSize: 14, width: "100%", outline: "none", fontFamily: "inherit", boxSizing: "border-box", ...style }}
    onFocus={e => e.target.style.borderColor = T.accent + "60"} onBlur={e => e.target.style.borderColor = T.border} {...props} />;
}

// ═══ PauseLogo (기존 유지) ═══
export function PhaseLogo({ size = 32, showText = true, color = T.text }) {
  const iconHeight = size * 0.85;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: size * 0.25, cursor: "pointer", userSelect: "none" }}>
      <svg height={iconHeight} viewBox="0 0 16 20" style={{ width: "auto", flexShrink: 0 }} fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="6" height="20" rx="2" fill={T.accent} />
        <rect x="10" y="0" width="6" height="20" rx="2" fill={T.accent} />
      </svg>
      {showText && (
        <span style={{ fontSize: size * 0.7, fontWeight: 900, color, letterSpacing: "-0.5px", lineHeight: 1, fontFamily: "'Pretendard', sans-serif" }}>
          Pause
        </span>
      )}
    </div>
  );
}

// ═══ LockedOverlay (리디자인: 이모지 → Lucide Lock) ═══
export function LockedOverlay({ feature, plan, onUpgrade }) {
  const info = FEATURE_PLAN[feature];
  const tier = TIER[plan] || TIER.free;
  const requiredLevel = TIER[info?.plan]?.level || 0;
  if (tier.level >= requiredLevel) return null;

  return (
    <div style={{ position: "relative" }}>
      <div style={{ filter: "blur(3px)", opacity: 0.3, pointerEvents: "none" }}>
        <div style={{ height: 80, background: T.bg, borderRadius: 10 }} />
      </div>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" }}
        onClick={() => onUpgrade?.(feature)}>
        <LucideIcons.Lock size={20} color={T.textMuted} />
        <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{info?.title || "잠긴 기능"}</span>
        <span className="label-micro" style={{ padding: "3px 10px", background: T.accent + "15", color: T.accent, borderRadius: 4 }}>{(info?.plan || "starter").toUpperCase()} 이상</span>
      </div>
    </div>
  );
}
